import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { Logo } from "../Logo";
import { scrollToSection } from "../../lib/api";

const LINKS = [
  { id: "il-locale", label: "Il Locale", testid: "nav-link-about" },
  { id: "menu", label: "Menu", testid: "nav-link-menu" },
  { id: "galleria", label: "Galleria", testid: "nav-link-gallery" },
  { id: "eventi", label: "Eventi", testid: "nav-link-events", eventsOnly: true },
  { id: "dove-siamo", label: "Dove Siamo", testid: "nav-link-location" },
  { id: "contatti", label: "Contatti", testid: "nav-link-contacts" },
];

export function Navbar({ hasEvents }) {
  const [open, setOpen] = useState(false);
  const links = LINKS.filter((l) => !l.eventsOnly || hasEvents);
  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(`#${id}`), open ? 250 : 0);
  };

  return (
    <>
      <header data-testid="redstone-navbar" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-obsidian/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 sm:py-2.5 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="text-[11px] font-sans font-semibold uppercase tracking-[0.22em] text-cream-muted hover:text-gold-glow transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-book-button"
              onClick={() => go("prenota")}
              className="hidden sm:inline-flex items-center gap-2 bg-burgundy-brand hover:bg-burgundy-glow text-cream text-[11px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border border-gold/30 hover:border-gold/60 transition-colors duration-300"
            >
              Prenota un tavolo
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
              className="lg:hidden text-cream p-2"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-obsidian/97 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 py-3.5 border-b border-white/10">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="text-cream p-2">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-3xl sm:text-4xl text-cream hover:text-gold-glow transition-colors py-2"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -28 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + links.length * 0.06, duration: 0.5 }}
                data-testid="mobile-menu-book-cta"
                onClick={() => go("prenota")}
                className="mt-6 self-start bg-burgundy-brand text-cream text-xs font-bold uppercase tracking-[0.25em] px-8 py-4 rounded-full border border-gold/30"
              >
                Prenota un tavolo
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
